import { useRef } from 'react';

import { DBSchema, IDBPDatabase, openDB } from 'idb';

import { LogEvents, logger } from 'lib/common/components/LoggerController';

import Context, { LogEntry } from './Context';

interface LogBufferDB extends DBSchema {
  logs: {
    key: number; // timestamp
    value: LogEntry;
    indexes: { 'by-timestamp': number };
  };
}

const DB_NAME = 'LOG_BUFFER_DB';
const LOG_STORE = 'logs';

class LogStore {
  private dbPromise: Promise<IDBPDatabase<LogBufferDB>>;

  private pruneInterval: NodeJS.Timeout;

  constructor() {
    this.dbPromise = openDB<LogBufferDB>(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(LOG_STORE)) {
          const logStore = db.createObjectStore(LOG_STORE, {
            keyPath: 'timestamp',
          });
          logStore.createIndex('by-timestamp', 'timestamp');
        }
      },
    });

    this.pruneOldLogs();

    this.pruneInterval = setInterval(
      () => this.pruneOldLogs(),
      60 * 60 * 1000 // Every hour
    );
  }

  async deleteFromIndex(queryValue: IDBKeyRange) {
    try {
      const db = await this.dbPromise;

      const tx = db.transaction(LOG_STORE, 'readwrite');
      const store = tx.objectStore(LOG_STORE);
      const index = store.index('by-timestamp');

      let cursor = await index.openCursor(queryValue);

      while (cursor) {
        await cursor.delete();
        cursor = await cursor.continue();
      }

      await tx.done;
    } catch (error) {
      logger.error(LogEvents.LOGGING.PRUNING_LOGS_FAILED, { error });
    }
  }

  async pruneOldLogs() {
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

    await this.deleteFromIndex(IDBKeyRange.upperBound(twentyFourHoursAgo));
  }

  async add(message: string) {
    const db = await this.dbPromise;
    const entry: LogEntry = { message, timestamp: Date.now() };
    await db.add(LOG_STORE, entry);
  }

  async getLogsToSend(limit = 10000): Promise<LogEntry[]> {
    const db = await this.dbPromise;

    return db.getAllFromIndex(LOG_STORE, 'by-timestamp', undefined, limit);
  }

  async markLogsAsSentAndPrune(latestTimestamp: number) {
    await this.deleteFromIndex(IDBKeyRange.upperBound(latestTimestamp));
  }

  destroy() {
    clearInterval(this.pruneInterval);
  }
}

export const LogStoreProvider = ({ children }) => {
  const logStoreRef = useRef(new LogStore());

  return (
    <Context.Provider
      value={{
        actions: {
          // It is very unlikely that these methods will throw, but we catch errors in the calling
          // code just in case because we don't want logging failures to impact the main app functionality
          add: async (message: string) => {
            try {
              return await logStoreRef.current.add(message);
            } catch (error) {
              console.error('Failed to add log entry', error);
            }
          },
          getLogsToSend: async () => {
            try {
              return await logStoreRef.current.getLogsToSend();
            } catch (error) {
              console.error('Failed to get logs to send', error);
              return [];
            }
          },
          markLogsAsSentAndPrune: async (latestTimestamp: number) => {
            try {
              return await logStoreRef.current.markLogsAsSentAndPrune(latestTimestamp);
            } catch (error) {
              console.error('Failed to mark logs as sent and prune', error);
            }
          },
        },
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default LogStoreProvider;
